import {InlineAlertType} from '@tableau/widgets';
import {FormikContextType} from 'formik';
import React from 'react';
import {useEffect, useState} from 'react';
import ReactMarkdown from 'react-markdown';

import {FeatureFlags} from '~commonInfra/featureFlags/FeatureFlags';
import {ToasterService, ToastType} from '~commonInfra/services/ToasterService';
import {SessionServerSelectors} from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import {Messages} from '~src/codegen/Localize';
import { GaiErrorCode } from '~src/commonInfra/utils/ErrorCodes';
import { OAuthCredentialTypes } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { AddOAuthCredentialAction } from '~src/connectivity/services/AddOAuthCredentialAction';
import {makeJsonRestApiRequest, RestApiErrorResponse} from '~src/httpClient/RestApiService';
import {
  GenerativeAISectionProps,
} from '~src/settings/general-settings/components/sections/tableau-gai/GenerativeAISection';
import {useSelector} from '~src/settings/general-settings/utils';

export interface GenerativeAiRegistrationState {
  registered: boolean;
  domain?: string;
  username?: string;
  orgId?: string;
}

interface GaiResponse {
  generativeAiRegistration: GenerativeAiRegistrationState;
}


// Helper function to convert markdown text to React elements using ReactMarkdown
function parseErrorText(text: string): React.ReactNode {
  return (
    <ReactMarkdown
      source={text}
      linkTarget="_blank"
      className="gai-error-markdown"
    />
  );
}

export type GenerativeAIRegistrationHookType = {
  registrationState: GenerativeAiRegistrationState;
  unregisterEnterprise: () => Promise<void>;
  registerEnterprise: () => Promise<void>;
  alertMessage: { type: InlineAlertType | null, text: React.ReactNode, dismissible?: boolean } | null;
  setAlertMessage: (message: { type: InlineAlertType, text: React.ReactNode, dismissible?: boolean } | null) => void;
  resetAlertMessage: () => void;
}

export function gaiErrorMessageFor(errorResponse: RestApiErrorResponse): React.ReactNode {
  const errorCode = parseInt(errorResponse.data.error.code, 10);
  let messageText: string;

  switch(errorCode) {
    case GaiErrorCode.TABGPT_USERINFO_CONNECTION_EXCEPTION:
      messageText = Messages.gaiErrorUserInfoConnectionFailed();
      break;
    case GaiErrorCode.TABGPT_CUSTOMIZE_APPLICATION_PERMISSION_EXCEPTION:
      messageText = Messages.gaiErrorQueryConnectionFailed();
      break;
    case GaiErrorCode.TABGPT_EGPT_DISABLED_EXCEPTION:
      messageText = Messages.gaiErrorEgptRegistrationFailed();
      break;
    default:
      messageText = Messages.tableauGAISiteSettingsRegisterError();
      break;
  }

  return parseErrorText(messageText);
}

export function useGenerativeAIRegistrationHook(formik: FormikContextType<GenerativeAISectionProps>): GenerativeAIRegistrationHookType {

  const [registrationState, setRegistrationState]
    = useState<GenerativeAiRegistrationState>({registered: false});

  const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);

  const requestState= FeatureFlags.tabGAITokenService &&
                            FeatureFlags.tabGAITokenServiceEnterpriseRegistration && isOnlineEnvironment;

  const [alertMessage, setAlertMessage] = React.useState<{ type: InlineAlertType | null, text: React.ReactNode, dismissible?: boolean }>({ type: null, text: '', dismissible: true });
  useEffect(() => {
    // Server has no Salesforce token service — treat as always registered.
    if (!isOnlineEnvironment) {
      setRegistrationState({ registered: true });
      return;
    }
    // We only request the state when the FF is enabled
    if(requestState) {
      makeJsonRestApiRequest<void, GaiResponse>(
        {
          config: {
            restApiVersion: '3.24',
            httpMethod: 'GET',
            endpoint: `gai/registration`,
            serverWideRequest: false
          }, request: null, queryParams: null
        }
      ).then(
        x => {
          setRegistrationState(x.generativeAiRegistration);
          // Clear any existing alert messages on successful state load
          setAlertMessage(null);
        },
        (reason: any) => {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: Messages.tableauGAISiteSettingsReadError()});
        });
    }
  }, [requestState, isOnlineEnvironment]);

  async function unregisterEnterprise(): Promise<void> {
    makeJsonRestApiRequest<void, void>(
      {config: {
          restApiVersion: '3.24',
          httpMethod: 'DELETE',
          endpoint: `gai/registration`,
          serverWideRequest: false
        }, request: null, queryParams: null }
    ).then(
      _ => {
        setRegistrationState({registered:false});
        if(FeatureFlags.tabGAITokenServiceRegistration) {
          formik.resetForm({
            values: {
              ...formik.values,
              settings: {
                ...formik.values.settings,
                generativeAiWebAuthoring: false,
                generativeAiPrep: false,
                generativeAiPulse: false,
                generativeAiDataCatalog: false
          }}});
        }
        // Clear any existing alert messages on success
        setAlertMessage(null);
      },
      (reason: any) => {
        let errorMessage = Messages.tableauGAISiteSettingsUnregisterError();
        setAlertMessage({
          type: InlineAlertType.Error,
          text: parseErrorText(errorMessage),
          dismissible: false
        });
      });
  }

  async function registerEnterprise(): Promise<void> {
    // Clear any existing alert messages at the start of registration attempt
    setAlertMessage(null);
    try {
      const credentialId = await AddOAuthCredentialAction.attemptAuth(OAuthCredentialTypes.Salesforce, null, null, null, null, /* requiresUpgrade: */ true);

      const response = await makeJsonRestApiRequest<void, GaiResponse>({
        config: {
          restApiVersion: '3.24',
          httpMethod: 'PUT',
          endpoint: `gai/registration`,
          serverWideRequest: false
        }, request: null, queryParams: {id: credentialId} }
      );

      setRegistrationState(response.generativeAiRegistration);
      // Clear any existing alert messages on success
      setAlertMessage(null);
    } catch (error: any) {

      const errorMessage: React.ReactNode = error?.data?.error?.code
        ? gaiErrorMessageFor(error as RestApiErrorResponse)
        : parseErrorText(Messages.tableauGAISiteSettingsRegisterError());

      setAlertMessage({
        type: InlineAlertType.Error,
        text: errorMessage,
        dismissible: false
      });
    }
 }

  return {registrationState, unregisterEnterprise, registerEnterprise, alertMessage, setAlertMessage, resetAlertMessage: () => setAlertMessage(null)};

}
